(ns app.front.view.committees.admin
  (:require [app.domain.attachment.model :as attachment-model]
            [app.domain.committee.model :as committee-model]
            [app.domain.file.model :as file-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.file.upload :as upload]
            [app.front.ui :as ui]
            [app.front.ui.add-widget :as ui-add]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.section :as section]
            [app.front.ui.select :as ui-select]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.ui.tabs :as ui-tabs]
            [app.front.ui.text-button :as ui-text-button]
            [app.front.ui.typography :refer [h3]]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.committees.appeals :as appeals-admin]
            [app.front.view.committees.common :as committee-common]
            [app.front.view.committees.subs :as committee-subs]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.core :as acore]
            [app.front.view.group.admin.files :as files]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.admin.templates.subs :as template-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema.core :as s]))

;; Events

(rf/reg-event-db ::set-categories-for-attachment-types
  (fn [db [_ category value]]
    (assoc-in db [::attachment-type-categories category] value)))

(rf/reg-event-fx ::save-committee-category.success
  (fn [{:keys [db]} [_ category status resp]]
    {:db       (update db ::attachment-type-categories dissoc category)
     :dispatch [::entities/apply-changes status resp]}))

(rf/reg-event-fx ::save-categories-authority
  (fn [_ [_ group-id category attachment-types]]
    {:comm/command {:command    :attachment/save-committee-category-authority
                    :data       {:category           category
                                 :attachment-types   attachment-types
                                 :authority-group-id group-id}
                    :on-success [::save-committee-category.success category]
                    :notify     {:success :committee/attachment-categories-updated}}}))

(rf/reg-event-fx ::save-categories
  (fn [_ [_ group-id category attachment-types]]
    {:comm/command {:command    :attachment/save-committee-category
                    :data       {:category         category
                                 :attachment-types attachment-types
                                 :group-id         group-id}
                    :on-success [::save-committee-category.success category]
                    :notify     {:success :committee/attachment-categories-updated}}}))

(rf/reg-event-db ::set-show-inactive-committees?
  (fn [db [_ value]]
    (assoc db ::show-inactive-committees? value)))

;; Subs

(rf/reg-sub ::show-inactive-committees?
  :-> ::show-inactive-committees?)

(rf/reg-sub ::committees
  :<- [::committee-subs/committees]
  :<- [::gas/group-id]
  :<- [:language]
  (fn [[committees group-id lang] _]
    ;; show all committes including inactive 
    ;; sort by active first then by name
    ;; there is no created date
    (->> committees
         (filter #(-> % :entity/parent :db/id (= group-id)))
         (sort-by #(vector (if (du/in-state? :committee.state/active %) 0 1)
                           (-> %
                               (committee-model/committee-name lang)
                               str/lower-case))))))

(rf/reg-sub ::attachment-type-categories
  :-> ::attachment-type-categories)

(rf/reg-sub ::attachment-types-in-category
  (fn [db [_ category]]
    (get-in db [::attachment-type-categories category])))

(rf/reg-sub
  ::new-attachment-types
  :<- [::attachment-type-categories]
  :<- [::entities/entity-db]
  (fn [[attachment-type-categories edb] [_ category]]
    (when (get attachment-type-categories category)
      (->> (get attachment-type-categories category)
           (map #(get edb %))))))

(def committee-attrs
  {:name-attr :committee/names
   :desc-attr :committee/descriptions
   :name-fn   committee-model/committee-name
   :desc-fn   committee-model/committee-description})

(defn committee-item [{:keys [tr edit-event delete-event retract-event]} {id :db/id :as committee}]
  (let [status (<sub [::committee-subs/edit-item-status id])]
    [committee-common/list-item (merge committee-attrs
                                       {:item                     committee
                                        :status                   status
                                        :on-save                  #(>evt [edit-event %])
                                        :on-cancel                #(>evt [::committee-subs/set-edit-item-status id nil])
                                        :on-edit                  #(>evt [::committee-subs/set-edit-item-status id :editing])
                                        :on-delete                #(>evt [delete-event id])
                                        :active?                  (= :committee.state/active (:domain/state committee))
                                        :delete-title             (tr [:committee/delete-committee-confirm-title])
                                        :delete-confirm-content   (tr [:committee/delete-committee-confirm-content])
                                        :on-retract               #(>evt [retract-event id])
                                        :retractable?             (<sub [::committee-subs/retractable? id])
                                        :retract-title            (tr [:committee/retract-confirm-title])
                                        :retract-confirm-content  (tr [:committee/retract-confirm-content])
                                        :retract-not-allowed-info (tr [:committee/retract-not-allowed-info])})]))


(defn show-inactive-toggle []
  (let [tr             (<sub [:tr])
        show-inactive? (<sub [::show-inactive-committees?])]
    [:div.mb-6
     (cc/toggle {:id         "show-inactive-committees"
                 :label-text (tr [:committee/show-inactive])
                 :labelA     (tr [:no])
                 :labelB     (tr [:yes])
                 :on-toggle  #(>evt [::set-show-inactive-committees? %])
                 :toggled    (boolean show-inactive?)})]))


(defn committee-list [{:keys [edit-event delete-event retract-event]}]
  (let [tr         (<sub [:tr])
        committees (<sub [::committees])]
    (if (seq committees)
      [:ul
       (for-frag [committee committees]
         [committee-item {:tr            tr
                          :edit-event    edit-event
                          :delete-event  delete-event
                          :retract-event retract-event}
          committee])]
      [committee-common/empty-text (tr [(if (<sub [::show-inactive-committees?])
                                          :committee/no-committees
                                          :committee/no-active-committees)])])))


(defn add-committee [{:keys [create-event]}]
  (let [group-id (<sub [::gas/group-id])
        tr       (<sub [:tr])]
    [committee-common/add-widget
     {:state        (<sub [::committee-subs/add-committee-status])
      :on-start-add #(>evt [::committee-subs/show-add-committee])}
     [committee-common/add-entity-widget
      (merge committee-attrs
             {:on-create #(>evt [create-event group-id %])
              :on-cancel #(>evt [::committee-subs/hide-add-committee])
              :title     (tr [:committee/create-committee])})]]))


(defn category-selection [{:keys [category tr group-id old-attachment-types new-attachment-types available-types disabled? save-evt]}]
  (let [language (<sub [:language])]
    [:div.mb-4
     [:h3 (tr [category])]
     [ui-ms/multi-select {:id          (str (name category) "-type")
                          :values      available-types
                          :val->str    #(get % :label)
                          :on-change   #(>evt [::set-categories-for-attachment-types category (mapv :id %)])
                          :value       (->> (or new-attachment-types old-attachment-types)
                                            (map (fn [type]
                                                   {:id (:db/id type) :label (attachment-common/attachment-type-label type language)})))
                          :label-text  (attachment-common/types-str tr (or new-attachment-types old-attachment-types))
                          :hide-label? true}]
     [:div.text-right
      (cc/button {:on-click         #(>evt [save-evt group-id category (or (<sub [::attachment-types-in-category category])
                                                                           (map :db/id old-attachment-types))])
                  :icon             i/check
                  :icon-description ""
                  :disabled         disabled?
                  :class-name       "ml-2 mt-2"}
                 (tr [:save]))]]))

(rf/reg-sub ::old-attachment-types
  :<- [::gas/attachment-types]
  (fn [attachment-types [_ category]]
    (->> attachment-types
         (filter #(contains? (-> % :attachment-type/committee-category set) category)))))

(defn attachment-categories-editor []
  (let [tr       (<sub [:tr])
        group-id (<sub [::gas/group-id])]
    (if (<sub [::acore/fetching-config? group-id :group/attachment-types])
      (cc/loading-spinner {:with-overlay false
                           :small        true})
      (let [available-types (->> (<sub [::gas/attachment-types-with-i18n])
                                 (map (fn [[text id]]
                                        {:id id :label text})))]
        (for-frag [category attachment-model/committee-categories
                   :let [old-attachment-types (<sub [::old-attachment-types category])
                         new-attachment-types (<sub [::new-attachment-types category])
                         disabled?            (or (not new-attachment-types)
                                                  (= (du/entity-id-set old-attachment-types)
                                                     (du/entity-id-set new-attachment-types)))]]
          [category-selection (util/mmap {:save-evt ::save-categories-authority}
                                         category
                                         tr
                                         group-id
                                         old-attachment-types
                                         new-attachment-types
                                         available-types
                                         disabled?)])))))

;;TODO remove when :feature-flag.id/domain-level-meetings is removed
(defn authority-group-view
  "Committees admin view for authority groups"
  [match]
  (let [tr (<sub [:tr])]
    [acommon/admin-page
     (util/mmap {:title (tr [:view/group.admin.committees])} match)
     [:section
      [:h2 (tr [:committee/committees])]
      [show-inactive-toggle]
      [committee-list {:edit-event    ::committee-subs/edit-committee-legacy
                       :delete-event  ::committee-subs/delete-committee-legacy
                       :retract-event ::committee-subs/retract-committee-legacy}]
      [add-committee {:create-event ::committee-subs/create-committee-legacy}]]
     [:section
      [:h2 (tr [:committee/appeals-tribunals])]
      [appeals-admin/show-inactive-toggle]
      [appeals-admin/tribunals-list]
      [appeals-admin/add-tribunal]]
     [:section
      [:h2 (tr [:committee/attachment-categories])]
      [:p.font-light.whitespace-pre-wrap (tr [:committee/attachment-categories-info])]
      [attachment-categories-editor]]]))

(rf/reg-sub ::old-domain-attachment-types
  :<- [::gas/domain-attachment-types]
  (fn [attachment-types [_ category]]
    (->> attachment-types
         (filter #(contains? (-> % :attachment-type/committee-category set) category)))))

(rf/reg-sub ::new-domain-attachment-types
  :<- [::attachment-type-categories]
  :<- [::gas/domain-attachment-types]
  (fn [[attachment-type-categories attachment-types] [_ category]]
    (when (contains? attachment-type-categories category)
      (let [ids (-> (get attachment-type-categories category)
                    set)]
        (->> attachment-types
             (filter #(contains? ids (:db/id %))))))))

(defn domain-attachment-categories-editor []
  (let [tr       (<sub [:tr])
        group-id (<sub [::gas/group-id])]
    (if (<sub [::acore/fetching-config? group-id :group/domain-attachment-types])
      (cc/loading-spinner {:with-overlay false
                           :small        true})
      (let [available-types (->> (<sub [::gas/domain-available-attachment-types-with-i18n])
                                 (map (fn [[text id]]
                                        {:id id :label text})))]
        (for-frag [category attachment-model/committee-categories
                   :let [old-attachment-types (<sub [::old-domain-attachment-types category])
                         new-attachment-types (<sub [::new-domain-attachment-types category])
                         disabled?            (or (not new-attachment-types)
                                                  (= (du/entity-id-set old-attachment-types)
                                                     (du/entity-id-set new-attachment-types)))]]
          [category-selection (util/mmap {:save-evt ::save-categories}
                                         category
                                         tr
                                         group-id
                                         old-attachment-types
                                         new-attachment-types
                                         available-types
                                         disabled?)])))))

(defn authority-group-tribunals-view
  "Tribunals admin view for authority groups"
  [match]
  (let [tr (<sub [:tr])]
    [acommon/admin-page
     (util/mmap {:title (tr [:committee/appeals-tribunals])} match)
     [:section
      [appeals-admin/show-inactive-toggle]
      [appeals-admin/tribunals-list]
      [appeals-admin/add-tribunal]]]))

;;TODO remove when :feature-flag.id/domain-level-meetings is removed
(def committee-route
  {:name        :view/group.admin.committees
   :view        authority-group-view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::committee-subs/fetch-committees group-id])
                                  (>evt [::gas/fetch-attachment-types-in-use group-id])))}]})

(defn- meeting-type-save-disabled? [params]
  (or (str/blank? (get params :committee/name))
      (str/blank? (get params :committee/description))
      (nil? (get params :committee/agenda-template))
      (empty? (get params :committee/authority-groups))))

(defn- meeting-weekdays [tr]
  (mapv (fn [idx]
          {:id    idx
           :label (futil/tr-weekday tr (str (mod (inc idx) 7)))})
        (range 7)))

(defn- meeting-frequencies [tr]
  (mapv (fn [period]
          {:id    period
           :label (futil/tr-date-frequency tr (name period))})
        committee-model/schedule-periods))

;; Shared meeting type form fields
(defn meeting-type-form-fields [{:keys [group-id params tr id-prefix weekdays frequencies edit-mode domain-group-id]}]
  (let [domain-id                   (or domain-group-id group-id)
        available-authority-groups  (<sub [::committee-subs/available-authority-groups domain-id])
        available-agenda-templates  (<sub [::committee-subs/available-meeting-agenda-templates domain-id])
        available-package-templates (<sub [::committee-subs/available-package-templates domain-id])
        set-field-event             (if edit-mode ::committee-subs/set-edit-meeting-type-field ::committee-subs/set-meeting-type-field)]
    [:<>
     ;; Meeting Type Name
     [:div.mb-4
      [ui/text-input {:id         (str id-prefix "name")
                      :label-text (tr [:meeting-type/names])
                      :light      true
                      :required   true
                      :value      (get params :committee/name "")
                      :on-change  #(>evt [set-field-event group-id :committee/name (-> % .-target .-value)])}]]
     ;; Type Description
     [:div.mb-4
      [ui/text-area {:id         (str id-prefix "description")
                     :label-text (tr [:meeting-type/descriptions])
                     :light      true
                     :required   true
                     :rows       4
                     :value      (get params :committee/description "")
                     :on-change  #(>evt [set-field-event group-id :committee/description (-> % .-target .-value)])}]]
     ;; Templates and Authority Groups
     [:div.grid.grid-cols-3.gap-4.mb-4
      [:div
       (let [template-values (mapv (fn [t] {:id (:db/id t) :label (:meeting-agenda-template/name t)}) available-agenda-templates)
             selected-id     (get params :committee/agenda-template)
             selected-value  (some #(when (= (:id %) selected-id) %) template-values)]
         [ui-select/select {:id          (str id-prefix "agenda-template")
                            :label-text  (tr [:meeting/agenda-template])
                            :select-text (tr [:meeting/select-template])
                            :light       true
                            :required    true
                            :class-name  "w-full"
                            :values      template-values
                            :value       selected-value
                            :val->str    :label
                            :on-change   #(>evt [set-field-event group-id :committee/agenda-template (:id %)])}])]
      [:div
       (let [template-values (mapv (fn [t] {:id (:db/id t) :label (:package-pdf-template/name t)}) available-package-templates)
             selected-id     (get params :committee/packet-template)
             selected-value  (some #(when (= (:id %) selected-id) %) template-values)]
         [ui-select/select {:id          (str id-prefix "packet-template")
                            :label-text  (tr [:meeting/packet-template])
                            :select-text (tr [:meeting/select-template])
                            :light       true
                            :class-name  "w-full"
                            :values      template-values
                            :value       selected-value
                            :val->str    :label
                            :on-change   #(>evt [set-field-event group-id :committee/packet-template (:id %)])}])]
      [:div
       (let [auth-group-values (mapv (fn [g] {:id (:db/id g) :label (:group/name g)}) available-authority-groups)
             selected-ids      (or (get params :committee/authority-groups) #{})
             selected-values   (filterv #(contains? selected-ids (:id %)) auth-group-values)]
         [:<>
          [:label.bx--label.required-field {:for (str id-prefix "available-departments")}
           (tr [:meeting-type/authority-groups])]
          [ui-ms/multi-select {:id          (str id-prefix "available-departments")
                               :values      auth-group-values
                               :val->str    :label
                               :hide-label? true
                               :on-change   #(>evt [set-field-event group-id :committee/authority-groups
                                                    (->> % (map :id) set)])
                               :value       selected-values
                               :label-text  (tr [:meeting-type/authority-groups])}]])]]

     [:hr.border-gray-50]
     ;; Scheduling Rules
     [:div.mb-4
      [:h3.font-semibold.mb-3 (tr [:meeting/scheduling-rules])]
      [:div.grid.grid-cols-3.gap-4
       [:div
        [ui-select/select {:id          (str id-prefix "start-on")
                           :label-text  (tr [:meeting/start-on])
                           :select-text (tr [:meeting/select-day])
                           :light       true
                           :class-name  "w-full"
                           :values      weekdays
                           :value       (some #(when (= (:id %) (get params :committee/schedule-day-of-week)) %) weekdays)
                           :val->str    :label
                           :on-change   #(>evt [set-field-event group-id :committee/schedule-day-of-week (:id %)])}]]
       [:div
        [ui-select/select {:id         (str id-prefix "repeat-every")
                           :label-text (tr [:meeting/repeat-every])
                           :light      true
                           :class-name "w-full"
                           :values     (range 1 31)
                           :value      (get params :committee/schedule-frequency)
                           :val->str   str
                           :on-change  #(>evt [set-field-event group-id :committee/schedule-frequency %])}]]
       [:div
        [ui-select/select {:id         (str id-prefix "frequency")
                           :label-text (tr [:meeting/repeat-frequency])
                           :light      true
                           :class-name "w-full"
                           :values     frequencies
                           :value      (some #(when (= (:id %) (get params :committee/schedule-period)) %) frequencies)
                           :val->str   :label
                           :on-change  #(>evt [set-field-event group-id :committee/schedule-period (:id %)])}]]]]]))

(defn- build-meeting-type-params [params lang committee-id]
  (let [agenda-template  (get params :committee/agenda-template)
        packet-template  (get params :committee/packet-template)
        schedule-day     (get params :committee/schedule-day-of-week)
        schedule-freq    (get params :committee/schedule-frequency)
        schedule-period  (get params :committee/schedule-period)
        authority-groups (not-empty (get params :committee/authority-groups))]
    (-> {:committee/names        {lang (get params :committee/name)}
         :committee/descriptions {lang (get params :committee/description)}}
        (cond->
          committee-id
          (assoc :committee/schedule-day-of-week schedule-day
                 :committee/schedule-frequency schedule-freq
                 :committee/schedule-period schedule-period
                 :committee/authority-groups authority-groups
                 :committee/meeting-agenda-template-ref agenda-template
                 :committee/packet-template packet-template
                 :db/id committee-id)

          (not committee-id)
          (cond->
            schedule-day (assoc :committee/schedule-day-of-week schedule-day)
            schedule-freq (assoc :committee/schedule-frequency schedule-freq)
            schedule-period (assoc :committee/schedule-period schedule-period)
            authority-groups (assoc :committee/authority-groups authority-groups)
            agenda-template (assoc :committee/meeting-agenda-template-ref agenda-template)
            packet-template (assoc :committee/packet-template packet-template))))))

(defn meeting-type-editor [{:keys [tr committee lang]}]
  (let [id             (:db/id committee)
        params         (<sub [::committee-subs/edit-meeting-type-form-params id])
        active?        (= :committee.state/active (:domain/state committee))
        retractable?   (<sub [::committee-subs/retractable? id])
        save-disabled? (meeting-type-save-disabled? params)]
    (when (and id (empty? params))
      (>evt [::committee-subs/init-edit-meeting-type-form id committee lang]))
    [:div.bg-ui-violet-10.mb-4.border-solid.border-l-8.border-ui-violet.flex.flex-col.md:flex-row.items-start
     [:div.flex-1>div.py-6.px-8
      [:div.mb-4.flex.items-center
       (cc/toggle {:id         (str "meeting-type-active-toggle-" id)
                   :label-text (tr [:meeting-type/active])
                   :labelA     (tr [:no])
                   :labelB     (tr [:yes])
                   :toggled    active?
                   :on-toggle  #(if active?
                                  (>evt [::committee-subs/delete-committee id])
                                  (>evt [::committee-subs/activate-committee id]))})]
      [meeting-type-form-fields {:group-id        id
                                 :domain-group-id (-> committee :entity/parent :db/id)
                                 :params          params
                                 :tr              tr
                                 :id-prefix       (str "edit-meeting-type-" id "-")
                                 :weekdays        (meeting-weekdays tr)
                                 :frequencies     (meeting-frequencies tr)
                                 :edit-mode       true}]
      [:div.gap-4.flex.flex-col.justify-end.items-end
       [:div.flex.items-center
        (when (false? retractable?)
          [:span.italic.text-right.text-xs.mr-2 (tr [:committee/retract-not-allowed-info])])
        [ui/modal-confirmation-button
         {:tr                  tr
          :id                  (str "delete-meeting-type-" id)
          :modal-title         (tr [:meeting-type/delete-confirm-title])
          :trigger-button-opts {:icon             i/trash
                                :kind             :ghost
                                :icon-description ""}
          :trigger-button-text (tr [:delete])
          :no-button-text      (tr [:no])
          :yes-button-text     (tr [:yes])
          :on-yes-click        #(>evt [::committee-subs/retract-committee id])
          :disabled?           (not retractable?)}
         [:div (tr [:meeting-type/delete-confirm-content])]
         [:div.mt-2.font-bold (committee-model/committee-name committee lang)]]
        [ui/button {:on-click   #(do
                                   (>evt [::committee-subs/set-edit-item-status id nil])
                                   (>evt [::committee-subs/clear-edit-meeting-type-form id]))
                    :class-name "ml-2"
                    :kind       :secondary}
         (tr [:cancel])]
        [ui/button {:on-click   #(>evt [::committee-subs/edit-committee
                                        (build-meeting-type-params params lang id)])
                    :class-name "ml-2"
                    :disabled   save-disabled?}
         (tr [:save])]]]]]))


(defn- meeting-type-table-expanded
  "Expanded content for meeting type table row. Shows editor when editing, details otherwise."
  [tr lang committee]
  (let [id     (:db/id committee)
        status (<sub [::committee-subs/edit-item-status id])]
    (case status
      :editing [meeting-type-editor (util/mmap tr committee lang)]
      :in-progress [:div.p-6.justify-center.items-center.flex
                    (cc/loading-spinner {:with-overlay false})]
      (let [{:committee/keys [meeting-agenda-template-ref
                              packet-template
                              schedule-day-of-week
                              schedule-frequency
                              schedule-period
                              authority-groups]}          committee
            committee-desc                                (committee-model/committee-description committee lang)
            agenda-template                               (when meeting-agenda-template-ref
                                                            [{:label (:meeting-agenda-template/name meeting-agenda-template-ref)}])
            packet-template                               (when packet-template
                                                            [{:label (:package-pdf-template/name packet-template)}])]
        [:div.px-6.pb-6.pt-2
         (when committee-desc
           [:p.text-sm.pb-0.mb-4 committee-desc])
         (when (seq authority-groups)
           [:div.mb-6
            [:h3.mb-3.font-semibold (tr [:meeting-type/authority-groups])]
            [:div.flex.flex-wrap.gap-2
             (for-frag [auth-group authority-groups]
               ^{:key (:db/id auth-group)}
               [ui/tag {:type :round :color :blue} (:group/name auth-group)])]])
         [committee-common/templates-section (util/mmap {:agenda-template agenda-template
                                                         :packet-template packet-template}
                                                        tr)]
         [committee-common/scheduling-rules-section (util/mmap {:start-day    schedule-day-of-week
                                                                :repeat-count schedule-frequency
                                                                :frequency    schedule-period}
                                                               tr)]]))))

(defn- committee-group-names [committee]
  (->> (:committee/authority-groups committee)
       (map :group/name)
       sort))

(defn meeting-type-view []
  (let [tr         (<sub [:tr])
        lang       (<sub [:language])
        committees (<sub [::committees])]
    (if (seq committees)
      (sortable-table/table
        {:id            "meeting-types-table"
         :tr            tr
         :cols          [[(tr [:meeting-type/name])
                          #(committee-model/committee-name % lang)
                          {:sorting true}]
                         [(tr [:meeting-type/status])
                          #(tr [(:domain/state %)])
                          {:sorting true}]
                         [(tr [:meeting-type/department])
                          (fn [committee]
                            (for-frag [group (committee-group-names committee)]
                              [ui/tag {:type  :round
                                       :color :blue}
                               group]))
                          {:sorting (fn [c1 c2]
                                      (compare (str/join " " (committee-group-names c1))
                                               (str/join " " (committee-group-names c2))))}]]
         :items         committees
         :expanded-func (partial meeting-type-table-expanded tr lang)
         :make-actions  (fn [committee]
                          (let [id (:db/id committee)]
                            [{:title    (tr [:edit])
                              :on-click #(>evt [::committee-subs/set-edit-item-status id :editing])}]))
         :get-status    (fn [committee]
                          (when (= :committee.state/active (:domain/state committee))
                            :ok))})
      [committee-common/empty-text (tr [(if (<sub [::show-inactive-committees?])
                                          :meeting-type/no-types
                                          :meeting-type/no-active)])])))

(defn add-meeting-type-widget [{:keys [group-id tr lang]}]
  (let [params (<sub [::committee-subs/meeting-type-form-params group-id])]
    [:div#add-meeting-type-form.mb-4
     [committee-common/add-widget
      {:state (<sub [::committee-subs/add-meeting-type-status group-id])}
      (let [disabled? (meeting-type-save-disabled? params)]
        [ui-add/add-widget
         {:buttons [{:icon     i/remove
                     :text     (tr [:cancel])
                     :on-click #(>evt [::committee-subs/hide-add-meeting-type group-id])}
                    {:icon      i/plus
                     :disabled? disabled?
                     :text      (tr [:committee/create])
                     :on-click  #(>evt [::committee-subs/create-meeting-type group-id
                                        (build-meeting-type-params params lang nil)])}]}
         [meeting-type-form-fields {:group-id        group-id
                                    :domain-group-id group-id
                                    :params          params
                                    :tr              tr
                                    :id-prefix       "meeting-type-"
                                    :weekdays        (meeting-weekdays tr)
                                    :frequencies     (meeting-frequencies tr)}]])]]))

(defn meeting-type-form []
  (let [group-id (<sub [::gas/group-id])
        tr       (<sub [:tr])
        lang     (<sub [:language])]
    [:<>
     [add-meeting-type-widget (util/mmap group-id tr lang)]
     [meeting-type-view]]))

(defn meeting-tab []
  (let [group-id (<sub [::gas/group-id])
        tr       (<sub [::gas/group-tr])]
    [:<>
     [section/section
      "meeting-types"
      (util/mmap {:heading-extra [ui/button {:id       "add-meeting-type-button"
                                             :on-click #(do
                                                          (>evt [::section/toggle-collapsed (section/get-section-id "meeting-types") false])
                                                          (>evt [::committee-subs/show-add-meeting-type group-id])
                                                          (futil/scroll-to-id "add-meeting-type-form"))}
                                  (tr [:meeting/add-meeting-type])]}
                 tr)
      :meeting.configuration-subtitle
      [meeting-type-form]]
     [:section
      [:h2 (tr [:committee/attachment-categories])]
      [:p.font-light.whitespace-pre-wrap (tr [:committee/attachment-categories-info])]
      [domain-attachment-categories-editor]]]))

(defn- add-meeting-header-image [group-id file-id filename content-type md5]
  (comm/send-command-execution
    {:command    :file/add-group-file
     :data       {:group-id          group-id
                  :file-id           file-id
                  :file/filename     (str md5 "." (-> (str/split filename #"\.")
                                                      last))
                  :file/content-type content-type
                  :file/category     :file.category/form-image
                  :file/md5          md5}
     :on-success [::meeting-header-image-uploaded group-id]}))

(rf/reg-event-fx ::meeting-header-image-uploaded
  (fn [_ [_ group-id status response]]
    (let [file-id (ffirst response)]
      {:dispatch     [::entities/merge-entity-db status response]
       :comm/command {:command    :group/set-group-attribute
                      :data       {:group-id group-id
                                   :key      :group/form-pdf-header-image
                                   :file-id  file-id}
                      :on-success [::meeting-header-attribute-set]}})))

(rf/reg-event-fx ::set-meeting-header-contact-info
  (fn [_ [_ group-id new-text]]
    {:comm/command {:command    :group/set-group-attribute
                    :data       {:group-id group-id
                                 :key      :group/pdf-header-text
                                 :value    new-text}
                    :on-success [::meeting-header-attribute-set]}}))

(rf/reg-event-fx ::meeting-header-attribute-set
  (fn [_ [_ status response]]
    {:dispatch-n [[:notifications/add :toast :success :success ""]
                  [::entities/merge-entity-db status response]]}))

(defn meeting-header-contact-info [tr group]
  (r/with-let [*value (r/atom (:group/pdf-header-text group))]
    [:div.bx--col-xs-12.bx--col-md-6
     [:h4.font-bold.mb-2 (tr [:meeting/header-contact-info-title])]
     [ui-rt/editor-legacy {:initial-value (or @*value "")
                           :id            "meeting-header-contact-info"
                           :on-change     #(reset! *value %)}]
     [:div.flex.justify-end.mt-3
      (cc/button {:kind     :tertiary
                  :disabled (= @*value (:group/pdf-header-text group))
                  :on-click #(reset! *value (:group/pdf-header-text group))}
                 (tr [:cancel]))
      (cc/button {:kind     :primary
                  :disabled (= @*value (:group/pdf-header-text group))
                  :on-click #(>evt [::set-meeting-header-contact-info (:db/id group) @*value])}
                 (tr [:save]))]]))

(defn meeting-header-config [group-id]
  (let [tr    (<sub [:tr])
        group (<sub [::entities/entity group-id])
        image (-> group :group/form-pdf-header-image)]
    [acommon/config-section
     (tr [:meeting/header-config-title])
     (tr [:meeting/header-config-instruction])
     [:div.bx--row
      [:div.bx--col-xs-12.bx--col-md-6
       [:h4.font-bold.mb-2 (tr [:meeting/header-image-title])]
       (when image
         [:img.verdict-image-preview.mb-3 {:src (files/file-link (:db/id group) (:db/id image) false)}])
       [upload/upload-button "meeting-header-image" {:attach-file-fn   (partial add-meeting-header-image (:db/id group))
                                                     :button-i18n-key  :file/upload-new-image
                                                     :accept-types     ["image/*"]
                                                     :size-constraints {:bytes (file-model/size-limits :file.category/form-image)}}]]
      (when group
        [meeting-header-contact-info tr group])]]))

(defn- add-meeting-image [group-id existing-filenames file-id filename content-type md5]
  (if (contains? existing-filenames filename)
    (>evt [:notifications/add :toast :error :error/image-already-exists :error/image-must-be-unique])
    (comm/send-command-execution
      {:command    :file/add-group-file
       :data       {:group-id          group-id
                    :file-id           file-id
                    :file/filename     filename
                    :file/content-type content-type
                    :file/category     :file.category/meeting-image
                    :file/md5          md5}
       :on-success [::entities/merge-entity-db]})))

(defn meeting-images [group-id]
  (let [tr        (<sub [:tr])
        images    (<sub [::gas/domain-meeting-images group-id])
        filenames (set (map :file/filename images))]
    [acommon/config-section
     (tr [:meeting/images-title])
     (tr [:meeting/images-instruction])
     [:div
      [:div.mt-2.mb-4
       [upload/upload-button "meeting-template-image" {:attach-file-fn  (partial add-meeting-image group-id filenames)
                                                       :button-i18n-key :file/upload-new-image
                                                       :accept-types    ["image/*"]}]]
      (when (seq images)
        (sortable-table/table
          {:id            "meeting-images-table"
           :tr            tr
           :cols          [[(tr [:file/filename]) :file/filename {:sorting true}]
                           [(tr [:file/content-type]) :file/content-type {:sorting true}]]
           :items         images
           :id-func       :db/id
           :expanded-func (fn [{:db/keys [id]}]
                            [:img.verdict-image-preview {:src (files/file-link group-id id)}])
           :make-actions  (fn [{:db/keys [id]}]
                            [{:title           (tr [:image/delete])
                              :confirm?        true
                              :confirm-content (tr [:image/delete-confirm])
                              :on-click        #(>evt [::files/delete-file group-id id])}])}))]]))

(defn meeting-templates-tab []
  (let [group-id  (<sub [::gas/group-id])
        tr        (<sub [::gas/group-tr])
        admin?    (<sub [::domain-group-admin?])
        templates (<sub [::template-subs/meeting-agenda-templates group-id])]
    [:<>
     [:section
      [:div
       [h3 "mb-2 text-lg" (tr [:committee-meeting/agenda-templates])]
       [:table.w-full
        [:tbody
         (for-frag [{:keys [id link text copy-fn remove-fn]} templates]
           [:tr
            [:td
             (if link
               (cc/link {:href link} text)
               [:span.leading-tight text])]
            (when (some? remove-fn)
              [:td.pl-3
               [ui/modal-confirmation-button
                {:trigger-button-text (tr [:remove])
                 :text-button?        true
                 :tr                  tr
                 :id                  (str "remove-template-" id)
                 :on-yes-click        remove-fn
                 :trigger-button-opts {:aria-label (str (tr [:remove]) " " text)}}]])
            (when (some? copy-fn)
              [:td.pl-3
               [ui-text-button/text-button {:on-click   copy-fn
                                            :aria-label (str (tr [:copy]) " " text)}
                (tr [:copy])]])])]]]
      [:div.mt-4
       (cc/link {:href (href/href :view/group.admin.template.new
                                  {:template-type :meeting-agenda-template
                                   :group-id      group-id})}
                (tr [:template-editor/add-new-meeting-agenda-template]))]]
     (when admin?
       [:<>
        [meeting-images group-id]
        [meeting-header-config group-id]])]))

(rf/reg-sub ::domain-group-admin?
  :<- [::gas/group-id]
  :<- [:session/domain-groups]
  :<- [:session/admin?]
  (fn [[group-id domain-groups system-admin?] _]
    (or system-admin?
        (some-> (p/find-first domain-groups {:db/id group-id})
                :group-roles
                set
                (contains? :group.role/domain-admin)))))

(def ^:private meeting-tab-routes
  [:view/group.admin.meetings
   :view/group.admin.meeting-templates])

(def ^:private meeting-tab-labels
  {:view/group.admin.meetings          :meeting/configuration-subtitle
   :view/group.admin.meeting-templates :meeting/template-subtitle})

(defn meeting-view [match]
  (let [tr         (<sub [::gas/group-tr])
        group-id   (-> match :parameters :path :group-id)
        route-name (-> match :data :name)
        switcher   [ui-tabs/tabs
                    {:selected-id route-name
                     :aria-label  (tr [:view/group.admin.meetings])
                     :tabs        (vec (for [route meeting-tab-routes]
                                         {:id    route
                                          :label (tr [(meeting-tab-labels route)])
                                          :href  (href/href route {:group-id group-id})}))}]]
    [acommon/admin-page-v2
     (util/mmap {:allowed-group-types #{:group.type/domain}
                 :header-content      switcher
                 :title               (tr [:view/group.admin.meetings])}
                match)
     [acommon/section-title-wrapper {:hide-title? true}
      (if (= :view/group.admin.meeting-templates route-name)
        [meeting-templates-tab]
        [meeting-tab])]]))

(def ^:private meeting-route-controllers
  [{:parameters {:path [:group-id]}
    :start      (fn [params]
                  (let [group-id (-> params :path :group-id)]
                    (>evt [::committee-subs/fetch-committees group-id])
                    (>evt [::gas/fetch-authority-groups group-id])
                    (>evt [::gas/fetch-domain-attachment-types group-id])
                    (>evt [::template-subs/fetch-templates group-id])))}])

(defn- meetings-parent-breadcrumb [{:keys [tr match]}]
  (let [params     (-> match :parameters :path)
        route-name (-> match :data :name)]
    [route-name params (tr [:view/group.admin.meetings])]))

(defn- meeting-types-leaf-breadcrumb [{:keys [tr match]}]
  (let [params (-> match :parameters :path)]
    [:view/group.admin.meetings params (tr [:meeting/configuration-subtitle])]))

(defn- meeting-templates-leaf-breadcrumb [{:keys [tr match]}]
  (let [params (-> match :parameters :path)]
    [:view/group.admin.meeting-templates params (tr [:meeting/template-subtitle])]))

(def meeting-route
  {:name        :view/group.admin.meetings
   :own-layout? true
   :view        meeting-view
   :breadcrumb  {:items [meetings-parent-breadcrumb meeting-types-leaf-breadcrumb]}
   :parameters  {:path {:group-id s/Int}}
   :controllers meeting-route-controllers})

(def meeting-templates-route
  {:name        :view/group.admin.meeting-templates
   :own-layout? true
   :view        meeting-view
   :breadcrumb  {:items [meetings-parent-breadcrumb meeting-templates-leaf-breadcrumb]}
   :parameters  {:path {:group-id s/Int}}
   :controllers meeting-route-controllers})

(def tribunal-route
  {:name        :view/group.admin.tribunals
   :view        authority-group-tribunals-view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::committee-subs/fetch-committees group-id])))}]})
